/* Custos — a estrutura de custos da empresa, aplicada a todos os projetos.
 *
 * Seis linhas fixas mais os extras livres. Cada uma resolve em reais por um de
 * quatro modos, e quem sabe interpretar esses modos é `custoConfig` do core:
 * nenhuma conta é refeita aqui. A prévia desta tela e o preço do relatório
 * saem da MESMA função, que foi o ponto da unificação.
 *
 * UM MÓDULO POR CAPÍTULO: `diretos` e `extras` são as duas tabelas,
 * `formulario` é o modal, `configuracao` é a leitura e escrita de uma
 * ConfigCusto. Este index guarda o estado e decide o que mostrar. */

import { ArrowRight } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Celula } from '@/componentes/celula';
import { RodapeFunil } from '@/componentes/rodape-funil';
import { Button } from '@/componentes/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card';
import { Dialog, DialogContent } from '@/componentes/ui/dialog';
import { Skeleton } from '@/componentes/ui/skeleton';
import {
  mensagemDoErro,
  useIntegrador,
  useProjetoAtual,
  useSalvarIntegrador,
  type ChaveCustoDireto,
  type ConfigCusto,
  type ConfigCustoExtra,
} from '@/dados';
import { Pagina } from '@/layout/pagina';

import { type Configuracoes, type Edicao } from './configuracao';
import { Diretos } from './diretos';
import { BarraExemplo, numeroDaLinha, referencia } from './exemplo';
import { Extras } from './extras';
import { FormularioCusto } from './formulario';

/** Exemplo inicial da prévia, substituído pelo projeto atual quando existe. */
const EXEMPLO_MODULOS = '12';
const EXEMPLO_KWP = '5';

export function Custos() {
  const integrador = useIntegrador();
  const projeto = useProjetoAtual();
  const salvar = useSalvarIntegrador();

  const [conf, setConf] = React.useState<Configuracoes | null>(null);
  const [edicao, setEdicao] = React.useState<Edicao | null>(null);
  const [erroSalvar, setErroSalvar] = React.useState<string | null>(null);

  const [exemploModulos, setExemploModulos] = React.useState(EXEMPLO_MODULOS);
  const [exemploKwp, setExemploKwp] = React.useState(EXEMPLO_KWP);
  const mexeuNoExemplo = React.useRef(false);
  // Id do extra recém-criado, para o campo de nome nascer com o cursor dentro.
  const focoNoExtra = React.useRef<string | null>(null);
  const jaUsouProjeto = React.useRef(false);

  // Carrega UMA vez. Depois disso o estado da tela é a fonte do que se vê: um
  // refetch no meio da edição de um extra recém-criado o apagaria da tela.
  React.useEffect(() => {
    if (conf !== null || integrador.isPending) return;
    setConf({
      campos: integrador.data?.configs?.campos ?? {},
      extras: integrador.data?.configs?.extras ?? [],
    });
  }, [conf, integrador.isPending, integrador.data]);

  // Prévia pré-preenchida com o projeto em edição, e só enquanto o usuário não
  // tocou nos campos: nunca sobrescrevemos o exemplo que ele escolheu.
  React.useEffect(() => {
    if (jaUsouProjeto.current || mexeuNoExemplo.current) return;
    const atual = projeto.data;
    if (!atual) return;
    jaUsouProjeto.current = true;
    const kwp = numeroDaLinha(atual.kwp_sistema);
    const modulos = numeroDaLinha(atual.quantidade_modulos);
    if (kwp > 0) setExemploKwp(String(kwp));
    if (modulos > 0) setExemploModulos(String(modulos));
  }, [projeto.data]);

  const kwpRef = referencia(exemploKwp);
  const modulosRef = referencia(exemploModulos);
  const semEmpresa = !integrador.isPending && !integrador.isError && integrador.data === null;

  /** Grava o bloco `configs` inteiro. Devolve se o servidor aceitou. */
  async function persistir(proximo: Configuracoes): Promise<boolean> {
    setConf(proximo);
    try {
      await salvar.mutateAsync({ configs: { campos: proximo.campos, extras: proximo.extras } });
      setErroSalvar(null);
      return true;
    } catch (erro) {
      setErroSalvar(mensagemDoErro(erro));
      return false;
    }
  }

  async function aplicar(cfg: ConfigCusto): Promise<boolean> {
    if (conf === null || edicao === null) return false;

    const idExtra = idDoExtra(edicao.chave);
    const proximo: Configuracoes =
      idExtra === null
        ? { ...conf, campos: { ...conf.campos, [edicao.chave]: cfg } }
        : {
            ...conf,
            // Substituição, não mesclagem: trocar de faixa para valor fixo tem
            // de DESCARTAR as faixas, senão elas voltam quando o modo mudar.
            extras: conf.extras.map((extra) =>
              extra.id === idExtra ? { id: extra.id, label: extra.label, ...cfg } : extra,
            ),
          };

    const ok = await persistir(proximo);
    if (ok) {
      setEdicao(null);
      toast.success('Custo salvo.');
    }
    return ok;
  }

  async function removerConfiguracao(): Promise<boolean> {
    if (conf === null || edicao === null) return false;
    const campos = { ...conf.campos };
    delete campos[edicao.chave as ChaveCustoDireto];
    const ok = await persistir({ ...conf, campos });
    if (ok) {
      setEdicao(null);
      toast.success('Configuração removida.');
    }
    return ok;
  }

  function adicionarExtra() {
    if (conf === null) return;
    // Só na tela: um extra sem nome ainda não é dado. Ele é gravado quando o
    // usuário o nomeia, o que evita uma escrita vazia por clique.
    const id = crypto.randomUUID();
    const novo: ConfigCustoExtra = { id, label: '', modo: 'fixo', taxa: 0 };
    setConf({ ...conf, extras: [...conf.extras, novo] });
    focoNoExtra.current = id;
  }

  function renomearExtra(id: string, nome: string) {
    if (conf === null) return;
    const atual = conf.extras.find((extra) => extra.id === id);
    if (!atual || (atual.label ?? '') === nome) return;
    const extras = conf.extras.map((extra) => (extra.id === id ? { ...extra, label: nome } : extra));
    // Extra sem rótulo é ignorado pelo motor: não vale uma ida ao servidor.
    if (nome === '') setConf({ ...conf, extras });
    else void persistir({ ...conf, extras });
  }

  function removerExtra(id: string) {
    if (conf === null) return;
    void persistir({ ...conf, extras: conf.extras.filter((extra) => extra.id !== id) });
  }

  return (
    <Pagina
      titulo="Estrutura de custos"
      descricao="O que entra no custo de cada projeto vendido, calculado a partir do tamanho do sistema."
      etapa="custos"
    >
      {integrador.isError ? (
        <Aviso titulo="Não foi possível carregar os custos">
          <p>{mensagemDoErro(integrador.error)}</p>
          <div>
            <Button variant="outline" size="sm" onClick={() => void integrador.refetch()}>
              Tentar de novo
            </Button>
          </div>
        </Aviso>
      ) : semEmpresa ? (
        <Card>
          <CardHeader>
            <CardTitle>Cadastre a empresa primeiro</CardTitle>
            <CardDescription>
              Os custos são da empresa, não de um projeto. Informe o nome da empresa em Empresa e
              volte para configurá-los.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/empresa">Ir para Empresa</Link>
            </Button>
          </CardContent>
        </Card>
      ) : conf === null ? (
        <div className="favo">
          <Skeleton className="h-14 w-full rounded-celula" />
          <Skeleton className="h-72 w-full rounded-celula" />
        </div>
      ) : (
        <div className="favo">
          <BarraExemplo
            modulos={exemploModulos}
            kwp={exemploKwp}
            aoMudarModulos={(valor) => {
              mexeuNoExemplo.current = true;
              setExemploModulos(valor);
            }}
            aoMudarKwp={(valor) => {
              mexeuNoExemplo.current = true;
              setExemploKwp(valor);
            }}
          />

          {erroSalvar ? (
            <Aviso titulo="Não foi possível salvar">
              <p>{erroSalvar}</p>
              <p>As alterações estão apenas nesta tela até que o servidor aceite.</p>
              <div>
                <Button variant="outline" size="sm" onClick={() => void persistir(conf)}>
                  Salvar de novo
                </Button>
              </div>
            </Aviso>
          ) : null}

          <Diretos
            campos={conf.campos}
            kwpRef={kwpRef}
            modulosRef={modulosRef}
            aoConfigurar={setEdicao}
          />

          <Extras
            extras={conf.extras}
            kwpRef={kwpRef}
            modulosRef={modulosRef}
            focoNoExtra={focoNoExtra}
            aoConfigurar={setEdicao}
            aoRenomear={renomearExtra}
            aoRemover={removerExtra}
            aoAdicionar={adicionarExtra}
          />

          <RodapeFunil etapa="custos">
            <Button asChild>
              <Link to="/despesas">
                Continuar
                <ArrowRight />
              </Link>
            </Button>
          </RodapeFunil>
        </div>
      )}

      {/* O formulário só existe enquanto o modal está aberto, e é remontado a
          cada abertura pela `key`. É essa montagem que garante estado limpo:
          era um modal reaproveitado que fazia as faixas de um campo aparecerem
          na configuração do campo seguinte. */}
      <Dialog
        open={edicao !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setEdicao(null);
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
          {edicao ? (
            <FormularioCusto
              key={edicao.chave}
              titulo={edicao.titulo}
              cfg={edicao.cfg}
              configurado={edicao.configurado}
              kwpRef={kwpRef}
              modulosRef={modulosRef}
              aoAplicar={aplicar}
              aoRemover={removerConfiguracao}
              aoCancelar={() => setEdicao(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Pagina>
  );
}

function Aviso({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Celula campo="telha" respiro="justo" role="alert" className="text-negativo grid gap-2 text-apoio">
      <strong className="font-semibold">{titulo}</strong>
      {children}
    </Celula>
  );
}


function idDoExtra(chave: string): string | null {
  return chave.startsWith('extra:') ? chave.slice('extra:'.length) : null;
}
